import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { PoliticalOrg } from '../types/PoliticalOrg';
import { supabase } from '../services/supabase';
import toast from 'react-hot-toast';
import { ExternalLink } from 'lucide-react';
import { trackPageView } from '../utils/analytics';

interface Campaign {
  id: string;
  title: string;
  description: string;
  image?: string;
  source_url?: string;
}

const SITE_URL = 'https://ballotr.com';
const MIN_CONTRIBUTION_AMOUNT = 1;
const MAX_CONTRIBUTION_AMOUNT = 1000;

const isValidAction = (value: string | null): value is 'support' | 'oppose' => {
  return value === 'support' || value === 'oppose';
};

const isOrgAvailableForContribution = (org: PoliticalOrg | null): boolean => {
  return Boolean(org?.is_active && org?.is_verified && org?.donation_url);
};

const Contribute: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const campaignId = searchParams.get('campaignId');
  const rawAction = searchParams.get('action');
  const action = isValidAction(rawAction) ? rawAction : null;
  const orgId = searchParams.get('orgId');

  const [selectedOrg, setSelectedOrg] = useState<PoliticalOrg | null>(
    location.state?.selectedOrg || null
  );
  const [campaign, setCampaign] = useState<Campaign | null>(null);

  const [amount, setAmount] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);

  const orgLogoSrc = useMemo(() => {
    return selectedOrg?.logo_url || selectedOrg?.logo || '/assets/default-logo.png';
  }, [selectedOrg]);

  const orgSupportCopy = useMemo(() => {
    if (action === 'oppose') {
      return 'Direct your opposition through this organization.';
    }

    return 'Direct your contribution to strengthen your voice.';
  }, [action]);

  useEffect(() => {
    trackPageView('contribute');
  }, []);

  useEffect(() => {
    if (!campaignId || !action || !orgId || !user) {
      navigate('/');
    }
  }, [campaignId, action, orgId, user, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!campaignId || !orgId || !action || !user) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const [{ data: orgData, error: orgError }, { data: campaignData, error: campaignError }] =
          await Promise.all([
            supabase.from('political_orgs').select('*').eq('id', orgId).single(),
            supabase.from('campaigns').select('*').eq('id', campaignId).single(),
          ]);

        if (orgError || !orgData) {
          throw orgError || new Error('Could not load selected organization.');
        }

        if (campaignError || !campaignData) {
          throw campaignError || new Error('Could not load campaign data.');
        }

        const org = orgData as PoliticalOrg;

        if (!isOrgAvailableForContribution(org)) {
          toast.error('This organization is not available for contributions yet.');
          navigate('/');
          return;
        }

        setSelectedOrg(org);
        setCampaign(campaignData as Campaign);
      } catch (error) {
        console.error('[Contribute] Failed to load contribution context:', error);
        toast.error('Could not load contribution details.');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [campaignId, orgId, action, user, navigate]);

  const handleConfirm = () => {
    if (!amount || !selectedOrg) {
      toast.error('Please enter an amount.');
      return;
    }

    if (!campaignId || !action || !orgId) {
      toast.error('Missing contribution context.');
      return;
    }

    if (!isOrgAvailableForContribution(selectedOrg)) {
      toast.error('This organization is not available for contributions yet.');
      return;
    }

    const numericAmount = parseInt(amount, 10);

    if (
      Number.isNaN(numericAmount) ||
      numericAmount < MIN_CONTRIBUTION_AMOUNT ||
      numericAmount > MAX_CONTRIBUTION_AMOUNT
    ) {
      toast.error(
        `Please enter a whole dollar amount between $${MIN_CONTRIBUTION_AMOUNT} and $${MAX_CONTRIBUTION_AMOUNT}.`
      );
      return;
    }

    if (comment.trim().length > 750) {
      toast.error('Comment must be 750 characters or fewer.');
      return;
    }

    navigate(`/contribute/details?campaignId=${campaignId}&action=${action}&orgId=${orgId}`, {
      state: {
        campaign,
        selectedOrg,
        amount: numericAmount,
        comment: comment.trim(),
      },
    });
  };

  if (!campaignId || !action || !orgId || !selectedOrg || !user || loading) {
    return (
      <>
        <Helmet>
          <title>Contribution Details | Ballotr</title>
          <meta
            name="description"
            content="Prepare your contribution details before continuing to an external donation page."
          />
          <meta name="robots" content="noindex,nofollow" />
          <link rel="canonical" href={`${SITE_URL}/contribute`} />
        </Helmet>

        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-xl font-semibold mb-2">Loading contribution details...</h1>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Contribution Details | Ballotr</title>
        <meta
          name="description"
          content="Prepare your contribution details before continuing to an external donation page."
        />
        <meta name="robots" content="noindex,nofollow" />
        <link rel="canonical" href={`${SITE_URL}/contribute`} />
      </Helmet>

      <main className="bg-secondary min-h-screen py-8 px-4">
        <div className="max-w-lg mx-auto bg-white rounded-lg shadow-md p-6 space-y-6">
          <div>
            {campaign && (
              <div className="relative bg-gray-100 p-4 rounded-md">
                <h2
                  className={`text-md font-semibold mb-1 ${
                    action === 'support' ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  You are {action === 'support' ? 'Supporting' : 'Opposing'}:
                </h2>

                {campaign.source_url ? (
                  <a
                    href={campaign.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-black font-bold text-lg hover:underline"
                  >
                    {campaign.title}
                  </a>
                ) : (
                  <p className="text-black font-bold text-lg">{campaign.title}</p>
                )}

                <p className="text-sm text-gray-600 mt-1">{campaign.description}</p>

                {campaign.source_url && (
                  <a
                    href={campaign.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute bottom-2 right-2 text-gray-500 hover:text-blue-600"
                    title="Open source article"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col items-center">
            <img
              src={orgLogoSrc}
              alt={selectedOrg.name || 'Political Organization'}
              className="w-20 h-20 object-contain rounded-full border mb-4"
            />

            <h1 className="text-1xl font-semibold text-center">
              You are contributing to the {selectedOrg.name}
            </h1>

            <p className="text-sm text-textSecondary mt-2 text-center">{orgSupportCopy}</p>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-textPrimary mb-1">
                Contribution Amount ($)
              </label>

              <input
                id="amount"
                type="number"
                min={MIN_CONTRIBUTION_AMOUNT}
                max={MAX_CONTRIBUTION_AMOUNT}
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:ring-primary focus:border-primary"
                placeholder="Enter amount"
                required
              />

              <p className="mt-1 text-xs text-gray-500">
                Minimum: ${MIN_CONTRIBUTION_AMOUNT} • Maximum: $
                {MAX_CONTRIBUTION_AMOUNT.toLocaleString()}
              </p>
            </div>

            <div>
              <label htmlFor="comment" className="block text-sm font-medium text-textPrimary mb-1">
                Optional Comment
              </label>

              <textarea
                id="comment"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={750}
                className="w-full px-4 py-2 border rounded-md focus:ring-primary focus:border-primary"
                placeholder="Share your thoughts (optional)"
              />

              <p className="mt-1 text-xs text-gray-500">{comment.length}/750</p>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              <button
                onClick={handleConfirm}
                className="w-full bg-primary text-white py-2 rounded-md hover:bg-blue-600 transition"
              >
                Continue
              </button>

              <button
                onClick={() => navigate(-1)}
                className="w-full border border-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-100 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default Contribute;